import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { plural } from "@/lib/plural";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { Plus, Trash2, Check, X, Pencil } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// The kind is a stored key that never shows; this is the word on screen.
export const RUIMTE_KIND_LABELS = {
  washal: "Was-hal",
  pand: "Pand",
  technische_ruimte: "Technische ruimte",
  buitenterrein: "Buitenterrein",
  kantine: "Kantine",
  kantoor: "Kantoor",
  keuken: "Keuken",
  restaurant: "Restaurant",
  shop: "Shop",
  opslagruimte: "Opslagruimte",
  sanitair: "Sanitair",
  overig: "Ruimte",
};

const KIND_ORDER = [
  "washal", "pand", "technische_ruimte", "kantine", "kantoor", "keuken",
  "restaurant", "shop", "opslagruimte", "sanitair", "buitenterrein", "overig",
];

/** One editable label. Renaming a spoor carries its machines along, so it is a
 *  single field and a single save -- not a form to fill in twice. */
function EditableChip({ value, writable, onSave, removeSlot, testid, plain }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!writable) {
    return plain ? (
      <span className="text-body font-medium text-foreground" data-testid={testid}>{value}</span>
    ) : (
      <span className="rounded-md border border-border bg-muted/40 px-3 py-1 text-body text-foreground" data-testid={testid}>
        {value}
      </span>
    );
  }
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1">
        <Input
          autoFocus
          className="h-11 w-36"
          value={draft}
          data-testid={`${testid}-input`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); setEditing(false); onSave(draft); }
            if (e.key === "Escape") { setEditing(false); setDraft(value); }
          }}
        />
        <Button type="button" variant="ghost" size="icon" aria-label="Naam opslaan" data-testid={`${testid}-save`}
          onClick={() => { setEditing(false); onSave(draft); }}><Check size={18} /></Button>
        <Button type="button" variant="ghost" size="icon" aria-label="Annuleren"
          onClick={() => { setEditing(false); setDraft(value); }}><X size={18} /></Button>
      </span>
    );
  }
  if (plain) {
    return (
      <button
        type="button"
        data-testid={testid}
        className="inline-flex min-h-11 items-center gap-1.5 text-left text-body font-medium text-foreground"
        onClick={() => { setDraft(value); setEditing(true); }}
      >
        {value}
        <Pencil size={14} className="text-muted-foreground" />
      </button>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-muted/40 pl-1 pr-0.5">
      <button
        type="button"
        data-testid={testid}
        className="min-h-11 px-2 text-body text-foreground"
        onClick={() => { setDraft(value); setEditing(true); }}
      >
        {value}
      </button>
      {removeSlot}
    </span>
  );
}

export function RuimtesPanel({ locationId, locationType, ruimtes, assets, writable, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [renameAsk, setRenameAsk] = useState(null);
  const [newRuimte, setNewRuimte] = useState({ kind: "washal", name: "" });

  const counts = useMemo(() => {
    const map = {};
    (assets || []).forEach((a) => {
      if (a.ruimte_id) map[a.ruimte_id] = (map[a.ruimte_id] || 0) + 1;
    });
    return map;
  }, [assets]);

  const halls = ruimtes.filter((r) => r.kind === "washal");
  const others = ruimtes.filter((r) => r.kind !== "washal");

  const call = async (fn, okMessage) => {
    setBusy(true);
    try {
      const res = await fn();
      if (okMessage) toast.success(typeof okMessage === "function" ? okMessage(res) : okMessage);
      await onChanged();
      return true;
    } catch (e) {
      toast.error(getErrorMessage(e, "Dat lukte niet."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Renaming a spoor that carries work moves that history along. The backend
  // refuses with 409 until the app has asked, naming what moves.
  const saveSporen = async (ruimte, sporen, renameConfirmed = false) => {
    setBusy(true);
    try {
      const body = renameConfirmed ? { sporen, rename_confirmed: true } : { sporen };
      const res = await api.put(`/ruimtes/${ruimte.id}`, body);
      const moved = res?.data?.spoor_renamed || 0;
      toast.success(moved
        ? `Spoor aangepast, ${plural(moved, "machine", "machines")} mee bijgewerkt`
        : "Sporen bijgewerkt");
      setRenameAsk(null);
      await onChanged();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.rename) {
        setRenameAsk({ ruimte, sporen, message: detail.msg, rename: detail.rename });
      } else {
        toast.error(getErrorMessage(e, "Dat lukte niet."));
      }
    } finally {
      setBusy(false);
    }
  };

  const nextSpoor = (ruimte) => {
    const numbers = (ruimte.sporen || [])
      .map((s) => parseInt(String(s).replace(/\D+/g, ""), 10))
      .filter((n) => !Number.isNaN(n));
    const next = numbers.length ? Math.max(...numbers) + 1 : 1;
    return `Spoor ${next}`;
  };

  const addRuimte = async () => {
    const ok = await call(
      () => api.post("/ruimtes", {
        location_id: locationId,
        kind: newRuimte.kind,
        name: newRuimte.name.trim() || RUIMTE_KIND_LABELS[newRuimte.kind],
      }),
      "Ruimte toegevoegd"
    );
    // A failed save keeps the dialog and the typed name: retyping it is the
    // punishment for the server's problem otherwise.
    if (ok) {
      setAddOpen(false);
      setNewRuimte({ kind: "washal", name: "" });
    }
  };

  const addStandard = () =>
    call(
      () => api.post(`/locations/${locationId}/ruimtes/standaard`),
      (res) => (res?.data?.created
        ? `${plural(res.data.created, "ruimte", "ruimtes")} toegevoegd`
        : "Alle standaard ruimtes stonden er al")
    );

  return (
    <>
      {halls.length > 0 && (
        <Card className="p-5" data-testid="washallen-card">
          <h2 className="mb-1 text-body font-semibold text-foreground">Was-hallen</h2>
          <p className="mb-3 text-data text-muted-foreground">
            Het spoor hoort bij de machine die erin staat. Een naam wijzigen neemt de machines mee.
          </p>
          <div className="space-y-4">
            {halls.map((h) => (
              <div key={h.id} data-testid={`washal-${h.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <EditableChip
                    plain
                    value={h.name}
                    writable={writable}
                    testid={`washal-name-${h.id}`}
                    onSave={(v) => {
                      const trimmed = (v || "").trim();
                      if (!trimmed || trimmed === h.name) return;
                      call(() => api.put(`/ruimtes/${h.id}`, { name: trimmed }), "Naam bijgewerkt");
                    }}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-data text-muted-foreground">
                      {plural(counts[h.id] || 0, "machine", "machines")}
                    </span>
                    {writable && (
                      <DeleteConfirm
                        trigger={
                          <Button type="button" variant="ghost" size="icon" disabled={busy}
                            aria-label={`${h.name} weghalen`} data-testid={`washal-remove-${h.id}`}>
                            <Trash2 size={18} />
                          </Button>
                        }
                        title={`Was-hal "${h.name}" weghalen?`}
                        description="Dit kan niet ongedaan gemaakt worden. Zit er nog werk aan vast, dan weigert de app het."
                        confirmLabel="Weghalen"
                        testid={`washal-remove-confirm-${h.id}`}
                        onConfirm={() => call(() => api.delete(`/ruimtes/${h.id}`), `${h.name} weggehaald`)}
                      />
                    )}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {(h.sporen || []).map((sp) => {
                    const spoorTestid = `spoor-${h.id}-${sp.replace(/\s+/g, "-").toLowerCase()}`;
                    return (
                      <EditableChip
                        key={sp}
                        value={sp}
                        writable={writable}
                        testid={spoorTestid}
                        onSave={(v) => {
                          const trimmed = (v || "").trim();
                          if (!trimmed || trimmed === sp) return;
                          saveSporen(h, (h.sporen || []).map((x) => (x === sp ? trimmed : x)));
                        }}
                        removeSlot={writable ? (
                          <DeleteConfirm
                            trigger={
                              <Button type="button" variant="ghost" size="icon" disabled={busy}
                                aria-label={`${sp} weghalen`} data-testid={`${spoorTestid}-remove`}>
                                <Trash2 size={18} />
                              </Button>
                            }
                            title={`${sp} weghalen uit ${h.name}?`}
                            description="Staat er nog een machine, storing of reparatie op dit spoor, dan weigert de app het."
                            confirmLabel="Weghalen"
                            testid={`${spoorTestid}-remove-confirm`}
                            onConfirm={() => saveSporen(h, (h.sporen || []).filter((x) => x !== sp))}
                          />
                        ) : null}
                      />
                    );
                  })}
                  {(h.sporen || []).length === 0 && (
                    <span className="text-body text-muted-foreground">Nog geen sporen.</span>
                  )}
                  {writable && (
                    <Button type="button" variant="secondary" size="sm" disabled={busy}
                      data-testid={`spoor-add-${h.id}`}
                      onClick={() => saveSporen(h, [...(h.sporen || []), nextSpoor(h)])}>
                      <Plus size={18} className="mr-1.5" /> Spoor
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5" data-testid="ruimtes-card">
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
          <div>
            <h2 className="text-body font-semibold text-foreground">Ruimtes</h2>
            <p className="text-data text-muted-foreground">
              Waar iets kan staan op deze locatie. Een machine hangt aan een ruimte, een storing ook.
            </p>
          </div>
          {writable && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="secondary" size="sm" disabled={busy}
                data-testid="ruimtes-standaard-button" onClick={addStandard}>
                Vul aan vanuit standaard
              </Button>
              <Button type="button" size="sm" disabled={busy}
                data-testid="ruimte-add-button" onClick={() => setAddOpen(true)}>
                <Plus size={18} className="mr-1.5" /> Ruimte
              </Button>
            </div>
          )}
        </div>

        {others.length === 0 ? (
          <p className="text-body text-muted-foreground" data-testid="ruimtes-empty">
            {ruimtes.length === 0
              ? "Deze locatie heeft nog geen ruimtes, dus een machine kan er nergens staan."
              : "Buiten de was-hallen zijn hier nog geen ruimtes."}
            {writable ? " Vul ze aan vanuit de standaard voor dit soort locatie." : ""}
          </p>
        ) : (
          <div className="divide-y divide-border" data-testid="ruimtes-list">
            {others
              .slice()
              .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || (a.sort_order || 0) - (b.sort_order || 0))
              .map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2" data-testid={`ruimte-row-${r.id}`}>
                  <div className="min-w-0">
                    <EditableChip
                      plain
                      value={r.name}
                      writable={writable}
                      testid={`ruimte-name-${r.id}`}
                      onSave={(v) => {
                        const trimmed = (v || "").trim();
                        if (!trimmed || trimmed === r.name) return;
                        call(() => api.put(`/ruimtes/${r.id}`, { name: trimmed }), "Naam bijgewerkt");
                      }}
                    />
                    <div className="text-data text-muted-foreground">
                      {[
                        // Only say the soort when the name does not already say it.
                        (RUIMTE_KIND_LABELS[r.kind] || RUIMTE_KIND_LABELS.overig) === r.name
                          ? null
                          : RUIMTE_KIND_LABELS[r.kind] || RUIMTE_KIND_LABELS.overig,
                        plural(counts[r.id] || 0, "machine", "machines"),
                      ].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {writable && (
                    <DeleteConfirm
                      trigger={
                        <Button type="button" variant="ghost" size="icon" disabled={busy}
                          aria-label={`${r.name} weghalen`} data-testid={`ruimte-remove-${r.id}`}>
                          <Trash2 size={18} />
                        </Button>
                      }
                      title={`Ruimte "${r.name}" weghalen?`}
                      description="Dit kan niet ongedaan gemaakt worden. Zit er nog werk aan vast, dan weigert de app het."
                      confirmLabel="Weghalen"
                      testid={`ruimte-remove-confirm-${r.id}`}
                      onConfirm={() => call(() => api.delete(`/ruimtes/${r.id}`), `${r.name} weggehaald`)}
                    />
                  )}
                </div>
              ))}
          </div>
        )}
        {locationType === "truckwash" && halls.length === 0 && (
          <p className="mt-3 text-data text-muted-foreground" data-testid="washal-missing-hint">
            Deze truckwash heeft nog geen was-hal, dus er is geen spoor om een machine op te zetten.
          </p>
        )}
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" srDescription="Voeg een ruimte toe aan deze locatie: kies het soort en pas de naam aan.">
          <DialogHeader><DialogTitle>Ruimte toevoegen</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label>Soort</Label>
              <Select value={newRuimte.kind} onValueChange={(v) => setNewRuimte((n) => ({ ...n, kind: v }))}>
                <SelectTrigger data-testid="ruimte-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KIND_ORDER.map((k) => (
                    <SelectItem key={k} value={k}>{RUIMTE_KIND_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ruimte-naam">Naam</Label>
              <Input id="ruimte-naam" data-testid="ruimte-name-input"
                value={newRuimte.name}
                placeholder={RUIMTE_KIND_LABELS[newRuimte.kind]}
                onChange={(e) => setNewRuimte((n) => ({ ...n, name: e.target.value }))} />
              <p className="text-data text-muted-foreground">
                Leeg laten mag: dan heet de ruimte {RUIMTE_KIND_LABELS[newRuimte.kind].toLowerCase()}.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>Annuleren</Button>
            <Button data-testid="ruimte-save-button" disabled={busy} onClick={addRuimte}>Toevoegen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!renameAsk} onOpenChange={(v) => { if (!v) setRenameAsk(null); }}>
        <AlertDialogContent srDescription="Bevestig het wijzigen van de spoornaam of annuleer.">
          <AlertDialogHeader>
            <AlertDialogTitle>Spoornaam wijzigen?</AlertDialogTitle>
            <AlertDialogDescription data-testid="spoor-rename-message">
              {renameAsk?.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              data-testid="spoor-rename-confirm"
              onClick={() => saveSporen(renameAsk.ruimte, renameAsk.sporen, true)}
            >
              Naam wijzigen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
